import { useCallback, useEffect, useRef, useState, type KeyboardEvent, type PointerEvent, type ReactNode } from 'react';
import { flushSync } from 'react-dom';

/** Spec §44: roughly 800–1200 ms. */
export const HOLD_MS = 1000;

type Phase = 'idle' | 'holding' | 'revealed';

/**
 * Press-and-hold private reveal (spec §44–§47). Private content is produced by
 * a render function that only runs while revealed, so it is never in the DOM
 * otherwise. Anything that could mean "no longer holding" hides it at once:
 * release, cancel, window blur, page hidden.
 */
export function HoldToReveal({
  label,
  hint = 'Press and hold',
  children,
  className = '',
}: {
  label: string;
  hint?: string;
  children: () => ReactNode;
  className?: string;
}) {
  const [phase, setPhase] = useState<Phase>('idle');
  const timer = useRef<number | undefined>(undefined);

  const hide = useCallback(() => {
    window.clearTimeout(timer.current);
    timer.current = undefined;
    // Commit synchronously: the text must be gone before the next paint.
    flushSync(() => setPhase('idle'));
  }, []);

  const begin = useCallback(() => {
    window.clearTimeout(timer.current);
    setPhase('holding');
    timer.current = window.setTimeout(() => setPhase('revealed'), HOLD_MS);
  }, []);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') hide();
    };
    window.addEventListener('blur', hide);
    window.addEventListener('pagehide', hide);
    window.addEventListener('pointerup', hide);
    window.addEventListener('pointercancel', hide);
    window.addEventListener('touchend', hide);
    window.addEventListener('touchcancel', hide);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.clearTimeout(timer.current);
      window.removeEventListener('blur', hide);
      window.removeEventListener('pagehide', hide);
      window.removeEventListener('pointerup', hide);
      window.removeEventListener('pointercancel', hide);
      window.removeEventListener('touchend', hide);
      window.removeEventListener('touchcancel', hide);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [hide]);

  const onPointerDown = (e: PointerEvent<HTMLButtonElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();
    // Keep receiving the release even if the finger drifts or the overlay appears.
    e.currentTarget.setPointerCapture?.(e.pointerId);
    begin();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if ((e.key === ' ' || e.key === 'Enter') && !e.repeat) {
      e.preventDefault();
      begin();
    }
  };

  const onKeyUp = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === ' ' || e.key === 'Enter') hide();
  };

  return (
    <>
      <button
        type="button"
        className={`hold ${phase !== 'idle' ? 'hold-active' : ''} ${className}`}
        onPointerDown={onPointerDown}
        onPointerUp={hide}
        onPointerCancel={hide}
        onLostPointerCapture={hide}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
        onBlur={hide}
        onContextMenu={(e) => e.preventDefault()}
        aria-describedby={undefined}
      >
        <span className="hold-label">{label}</span>
        <span className="hold-hint">{hint}</span>
        <span className="hold-progress" aria-hidden="true" />
      </button>
      {phase === 'revealed' ? (
        <div className="reveal" role="status" aria-live="assertive">
          <div className="reveal-body">{children()}</div>
        </div>
      ) : null}
    </>
  );
}
